import React, { useState } from 'react';
import { Box, Text, Stack, Group, ActionIcon, MultiSelect } from '@mantine/core';
import { IconEdit, IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import { useComputedColorScheme } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { createLabelWithLink } from '../../utils/labelWithLink';

interface OrderedItem {
  _id: string;
  title: string;
  description?: string;
  qualification?: string;
  position?: string;
}

interface OrderedMultiSelectProps {
  items: OrderedItem[];
  allItems: OrderedItem[];
  selectedItems: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  title: string;
  orderTitle: string;
  placeholder: string;
  addItemText: string;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onEdit: (item: OrderedItem) => void;
  onAddNew: () => void;
}

export const OrderedMultiSelect: React.FC<OrderedMultiSelectProps> = ({
  items,
  allItems,
  selectedItems,
  onSelectionChange,
  title,
  orderTitle,
  placeholder,
  addItemText,
  onMoveUp,
  onMoveDown,
  onEdit,
  onAddNew,
}) => {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';
  const [animatingItems, setAnimatingItems] = useState<Set<string>>(new Set());

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const itemId = items[index]._id;
    setAnimatingItems((prev) => new Set(Array.from(prev).concat(itemId)));

    onMoveUp(index);

    setTimeout(() => {
      setAnimatingItems((prev) => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(itemId);
        return newSet;
      });
    }, 300);
  };

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return;
    const itemId = items[index]._id;
    setAnimatingItems((prev) => new Set(Array.from(prev).concat(itemId)));

    onMoveDown(index);

    setTimeout(() => {
      setAnimatingItems((prev) => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(itemId);
        return newSet;
      });
    }, 300);
  };

  return (
    <Stack gap="md">
      <MultiSelect
        placeholder={placeholder}
        data={allItems.map((item) => ({ value: item._id, label: item.title }))}
        value={selectedItems}
        onChange={onSelectionChange}
        searchable
        clearable
        {...createLabelWithLink({
          labelText: title,
          linkText: addItemText,
          onLinkClick: (e) => {
            e.preventDefault();
            onAddNew();
          },
        })}
      />

      {items.length > 0 && (
        <Box>
          <Text size="sm" fw={500} mb={2}>
            {orderTitle}
          </Text>
          <Stack gap="xs" style={{ position: 'relative' }}>
            {items.map((item, index) => (
              <div
                key={item._id}
                style={{
                  backgroundColor: isDark
                    ? 'var(--mantine-color-dark-6)'
                    : 'var(--mantine-color-gray-0)',
                  borderRadius: '4px',
                  border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px',
                  transition: animatingItems.has(item._id)
                    ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    : 'all 0.2s ease-in-out',
                  transform: 'translateY(0)',
                  opacity: animatingItems.has(item._id) ? 0.8 : 1,
                  scale: animatingItems.has(item._id) ? 1.02 : 1,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>
                    {item.title}
                  </Text>
                  {(item.description || item.qualification || item.position) && (
                    <Text size="xs" c="dimmed">
                      {item.qualification && item.position
                        ? `${item.qualification} • ${item.position}`
                        : item.description || item.qualification || item.position}
                    </Text>
                  )}
                </div>
                <Group gap="xs">
                  <ActionIcon
                    variant="light"
                    color="gray"
                    size="sm"
                    aria-label={t('common.moveUp')}
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <IconArrowUp size={14} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="gray"
                    size="sm"
                    aria-label={t('common.moveDown')}
                    onClick={() => handleMoveDown(index)}
                    disabled={index === items.length - 1}
                  >
                    <IconArrowDown size={14} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="teal"
                    size="sm"
                    aria-label={t('common.edit')}
                    onClick={() => onEdit(item)}
                  >
                    <IconEdit size={14} />
                  </ActionIcon>
                </Group>
              </div>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
